import React, { useState } from "react";
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';

import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddTicket from "../../Modals/addTicket";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { useGetFaqsQuery, useGetTicketsStatsQuery } from "../../service/main";

interface SupportData {
    id: number;
    title: string;
    description: string;
    submittedDate: string;
    status: "Pending" | "Resolved";
    adminReply?: string;
}

const AdminSupport = () => {
    const navigate = useNavigate();
    const [subTab, setSubTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowStatus, setSelectedRowStatus] =
        useState<"Pending" | "Resolved">("Pending");
    const { data: statsData } = useGetTicketsStatsQuery();
    const { data: faqsData } = useGetFaqsQuery();
    const stats = statsData?.data || {};
    const faqs = faqsData?.data?.list || [];

    const subTabs: ("Pending" | "Resolved")[] = ["Pending", "Resolved"];

    const [supportData, setSupportData] = useState<SupportData[]>([
        {
            id: 1,
            title: "App not loading",
            description: "The app freezes when I open it.",
            submittedDate: "20 Oct 2025",
            status: "Pending",
        },
        {
            id: 2,
            title: "Refund not processed",
            description: "I haven't received my refund yet.",
            submittedDate: "19 Oct 2025",
            status: "Resolved",
            adminReply: "Refund has been initiated and will reflect in 3–5 days.",
        },
        {
            id: 3,
            title: "Chat not connecting",
            description: "Unable to send or receive messages.",
            submittedDate: "18 Oct 2025",
            status: "Pending",
        },
    ]);

    const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSubTab(newValue);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Filter Data Based on Subtab
    const filteredData = supportData.filter(
        (item) => item.status === subTabs[subTab]
    );
    const [expanded, setExpanded] = React.useState<string | false>(false);

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
        };

    return (
        <>
            <div className="main_title">
                <h1>Admin Support Management </h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Admin Support Management
                </p>
            </div>
            <Box className="support_dashboard_cards">

                {/* TOP STATS */}
                <Box className="stats_row">
                    <div className="stat_card">
                        <figure className="icon">
                            <img src="/static/images/ticket.png" alt="Active Tickets" />
                        </figure>
                        <p className="label">Active Tickets</p>
                        <h2 className="value">{stats?.pending || 0}</h2>
                    </div>

                    <div className="stat_card">
                        <figure className="icon">
                            <img src="/static/images/greenCircleCheck.png" alt="Resolved" />
                        </figure>
                        <p className="label">Resolved</p>
                        <h2 className="value">{stats?.resolved || 0}</h2>
                    </div>

                    <div className="stat_card">
                        <figure className="icon">
                            <img src="/static/images/clockCircle.png" alt="Avg Response Time" />
                        </figure>
                        <p className="label">Avg Response Time</p>
                        <h2 className="value">{stats?.avgResponseTime || "-"}</h2>
                    </div>
                </Box>


                <ul className="add_campaign">
                    <li className="one" onClick={() => setOpenModal(true)}>
                        <AddIcon />
                        <h3>Create New Ticket</h3>
                        <p>Get help with an issue</p>
                    </li>
                    <li className="two" onClick={() => navigate("view")}>
                        <AnalyticsIcon />
                        <h3>View All Tickets</h3>
                        <p>Check status and replies</p>
                    </li>
                </ul>
                <div className="cards quick_help_section mt_20">
                    <h3 className="section_title">Quick Help Resources</h3>

                    <div className="quick_help_grid">
                        {/* Knowledge Base */}
                        <div className="quick_help_box cr_pt" onClick={() => navigate("knowledge-base")}>
                            <div className="icon">
                                <img src="/static/images/book_icon.svg" alt="Knowledge Base" />
                            </div>
                            <div className="text">
                                <h4>Knowledge Base</h4>
                                <p>Browse articles and guides</p>
                            </div>
                        </div>

                        {/* Video Tutorials */}
                        <div className="quick_help_box cr_pt" onClick={() => navigate("video-tutorial")}>
                            <div className="icon">
                                <img src="/static/images/video_icon.svg" alt="Video Tutorials" />
                            </div>
                            <div className="text">
                                <h4>Video Tutorials</h4>
                                <p>Learn with step-by-step videos</p>
                            </div>
                        </div>

                        {/* Email Support */}
                        <div className="quick_help_box">
                            <div className="icon">
                                <img src="/static/images/email_icon.svg" alt="Email Support" />
                            </div>
                            <div className="text">
                                <h4>Email Support</h4>
                                <p>support@boujeek.ae</p>
                            </div>
                        </div>

                        {/* Phone Support */}
                        <div className="quick_help_box">
                            <div className="icon">
                                <img src="/static/images/phone_icon.svg" alt="Phone Support" />
                            </div>
                            <div className="text">
                                <h4>Phone Support</h4>
                                <p>+971 4 444 3333</p>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="home_faq_sc mt_20">
                    <div className="container">
                        <div className="main_title">
                            <h1>Frequently asked questions</h1>
                        </div>
                        <div className="faq_lst hd_7">
                            {faqs.map((item: any) => (
                                <Accordion
                                    key={item.id}
                                    expanded={expanded === String(item.id)}
                                    onChange={handleChange(String(item.id))}
                                    className="faq_bx"
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <h3>{item.question}</h3>
                                    </AccordionSummary>
                                    <AccordionDetails>{item.answer}</AccordionDetails>
                                </Accordion>
                            ))}
                        </div>
                    </div>
                </section>
                {/* QUICK HELP */}
                {/* <h3 className="section_title">Quick Help Resources</h3>

                <div className="help_grid">
                    <div className="help_card">
                        <h4>Knowledge Base</h4>
                        <p>Browse articles & guides</p>
                    </div>

                    <div className="help_card">
                        <h4>Video Tutorials</h4>
                        <p>Learn with step-by-step videos</p>
                    </div>

                    <div className="help_card">
                        <h4>Email Support</h4>
                        <p>support@cloudnest.ae</p>
                    </div>

                    <div className="help_card">
                        <h4>Phone Support</h4>
                        <p>+971 4 444 3231</p>
                    </div>
                </div> */}

            </Box>   {/* Sub Tabs */}


            {/* Add Reply Modal */}
            <AddTicket
                open={openModal}
                onClose={handleCloseModal}
                setOpen={setOpenModal}
            />
        </>
    );
};

export default AdminSupport;
